import { useState, useEffect, useCallback } from 'react';
import { whatsappApi } from '../../../services/api';
import api from '../../../services/api';
import { toast } from 'react-hot-toast';
import { Loader2, Send, Upload, Users, ChevronLeft, ChevronRight, X, Gift, Download } from 'lucide-react';
import { getTypeLabel } from './constants';
import { useSearchParams } from 'react-router-dom';
import { usePageTitle } from '../../../hooks/usePageTitle';

const MAX_SELECTIONS = 10;

export default function DispatchTab() {
    usePageTitle('Disparar Mensagens');
    const [searchParams] = useSearchParams();
    const [loading, setLoading] = useState(false);
    const [loadingLeads, setLoadingLeads] = useState(false);
    const [templates, setTemplates] = useState<any[]>([]);
    const [selectedTemplate, setSelectedTemplate] = useState('');
    const [sourceType, setSourceType] = useState<'csv' | 'leads' | 'birthdays'>('csv');
    const [csvData, setCsvData] = useState<any[]>([]);
    const [selectedLeads, setSelectedLeads] = useState<number[]>([]);
    const [selectedLeadsData, setSelectedLeadsData] = useState<any[]>([]);
    const [leads, setLeads] = useState<any[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [pagination, setPagination] = useState({
        total: 0,
        totalPages: 0,
        limit: 10
    });

    useEffect(() => {
        loadTemplates();
    }, []);

    // Ler parâmetro source da URL para selecionar aniversariantes automaticamente
    useEffect(() => {
        const source = searchParams.get('source');
        if (source === 'birthdays') {
            setSourceType('birthdays');
        }
    }, [searchParams]);

    const loadLeads = useCallback(async () => {
        setLoadingLeads(true);
        try {
            const endpoint = sourceType === 'birthdays' 
                ? '/wheels/spins/birthdays' 
                : '/wheels/spins/all';
                
            const response = await api.get(endpoint, {
                params: {
                    page: currentPage,
                    limit: pagination.limit
                }
            });
            setLeads(response.data.spins || []);
            setPagination({
                total: response.data.pagination.total,
                totalPages: response.data.pagination.totalPages,
                limit: response.data.pagination.limit
            });
        } catch (error: any) {
            toast.error('Erro ao carregar leads');
        } finally {
            setLoadingLeads(false);
        }
    }, [currentPage, pagination.limit, sourceType]);


    useEffect(() => {
        if (sourceType === 'leads' || sourceType === 'birthdays') {
            loadLeads();
        }
    }, [currentPage, sourceType, loadLeads]);

    const loadTemplates = async () => {
        try {
            const response = await whatsappApi.getTemplates(true); // Only active
            setTemplates(response.templates || []);
        } catch (error: any) {
            toast.error('Erro ao carregar templates');
        }
    };


    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (event) => {
            const text = event.target?.result as string;
            const lines = text.split('\n');
            const headers = lines[0].split(',').map(h => h.trim());

            const data = lines.slice(1).filter(line => line.trim()).map(line => {
                const values = line.split(',').map(v => v.trim());
                const row: any = {};
                headers.forEach((header, index) => {
                    row[header] = values[index] || '';
                });
                return row;
            });

            if (data.length > MAX_SELECTIONS) {
                toast.error(`Você pode importar no máximo ${MAX_SELECTIONS} destinatários`);
                return;
            }

            setCsvData(data);
            toast.success(`${data.length} linhas carregadas`);
        };

        reader.readAsText(file);
    };

    const handleDispatch = async () => {
        if (!selectedTemplate) {
            toast.error('Selecione um template');
            return;
        }

        if (sourceType === 'csv' && csvData.length === 0) {
            toast.error('Carregue um arquivo CSV');
            return;
        }

        if (sourceType === 'leads' && selectedLeads.length === 0) {
            toast.error('Selecione pelo menos um lead');
            return;
        }

        if (sourceType === 'birthdays' && selectedLeads.length === 0) {
            toast.error('Selecione pelo menos um aniversariante');
            return;
        }

        setLoading(true);

        try {
            let response;

            if (sourceType === 'csv') {
                response = await whatsappApi.dispatchFromCSV(parseInt(selectedTemplate), csvData);
            } else {
                response = await whatsappApi.dispatchFromLeads(parseInt(selectedTemplate), selectedLeads);
            }

            toast.success('Disparo criado com sucesso!');

            // Start processing
            await whatsappApi.processDispatch(response.dispatch.id);
            toast.success('Envio iniciado!');

            // Reset form
            setCsvData([]);
            setSelectedLeads([]);
            setSelectedLeadsData([]);
            setSelectedTemplate('');
            setCurrentPage(1);

        } catch (error: any) {
            toast.error(error.error || 'Erro ao criar disparo');
        } finally {
            setLoading(false);
        }
    };

    const toggleLead = (leadId: number) => {
        const lead = leads.find(l => l.id === leadId);
        if (!lead) return;

        const isSelected = selectedLeads.includes(leadId);

        if (isSelected) {
            // Remove from selection
            setSelectedLeads(prev => prev.filter(id => id !== leadId));
            setSelectedLeadsData(prevData => prevData.filter(l => l.id !== leadId));
        } else {
            // Add to selection
            if (selectedLeads.length >= MAX_SELECTIONS) {
                toast.error(`Você pode selecionar no máximo ${MAX_SELECTIONS} clientes`);
                return;
            }
            setSelectedLeads(prev => [...prev, leadId]);
            setSelectedLeadsData(prevData => [...prevData, lead]);
        }
    };

    const handleSelectAllOnPage = () => {
        const currentPageLeadsNotSelected = leads.filter(l => !selectedLeads.includes(l.id));

        const availableSlots = MAX_SELECTIONS - selectedLeads.length;

        if (availableSlots === 0) {
            toast.error(`Você pode selecionar no máximo ${MAX_SELECTIONS} clientes`);
            return;
        }

        const leadsToAdd = currentPageLeadsNotSelected.slice(0, availableSlots);

        setSelectedLeads(prev => [...prev, ...leadsToAdd.map(l => l.id)]);
        setSelectedLeadsData(prevData => [...prevData, ...leadsToAdd]);

        if (leadsToAdd.length < currentPageLeadsNotSelected.length) {
            toast.error(`Apenas ${leadsToAdd.length} clientes foram selecionados (limite de ${MAX_SELECTIONS})`);
        } else {
            toast.success(`${leadsToAdd.length} clientes selecionados desta página`);
        }
    };

    const handleDeselectAllOnPage = () => {
        const currentPageLeadIds = leads.map(l => l.id);
        setSelectedLeads(prev => prev.filter(id => !currentPageLeadIds.includes(id)));
        setSelectedLeadsData(prevData => prevData.filter(l => !currentPageLeadIds.includes(l.id)));
    };

    const removeSelectedLead = (leadId: number) => {
        setSelectedLeads(prev => prev.filter(id => id !== leadId));
        setSelectedLeadsData(prevData => prevData.filter(l => l.id !== leadId));
    };

    const goToPage = (page: number) => {
        setCurrentPage(page);
    };

    return (
        <div className="space-y-6">
            <div className="bg-white">
                <h2 className="text-xl font-semibold mb-4">Disparar Mensagens</h2>

                {/* Template Selection */}
                <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Selecione o Template *
                    </label>
                    <select
                        value={selectedTemplate}
                        onChange={(e) => setSelectedTemplate(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="">Selecione...</option>
                        {templates.map((template) => (
                            <option key={template.id} value={template.id}>
                                {template.name} ({getTypeLabel(template.type)})
                            </option>
                        ))}
                    </select>
                </div>

                {/* Source Type */}
                <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Origem dos Destinatários
                    </label>
                    <div className="flex space-x-4">
                        <button
                            onClick={() => {
                                setSourceType('csv');
                                setSelectedLeads([]);
                                setSelectedLeadsData([]);
                                setCurrentPage(1);
                            }}
                            className={`flex-1 py-3 px-4 rounded-lg border-2 transition-colors ${sourceType === 'csv'
                                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                                    : 'border-gray-300 hover:border-gray-400'
                                }`}
                        >
                            <Upload className="h-5 w-5 mx-auto mb-1" />
                            Importar CSV
                        </button>
                        <button
                            onClick={() => {
                                setSourceType('leads');
                                setCsvData([]);
                                setSelectedLeads([]);
                                setSelectedLeadsData([]);
                                setCurrentPage(1);
                            }}
                            className={`flex-1 py-3 px-4 rounded-lg border-2 transition-colors ${sourceType === 'leads'
                                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                                    : 'border-gray-300 hover:border-gray-400'
                                }`}
                        >
                            <Users className="h-5 w-5 mx-auto mb-1" />
                            Selecionar Clientes
                        </button>
                        <button
                            onClick={() => {
                                setSourceType('birthdays');
                                setCsvData([]);
                                setSelectedLeads([]);
                                setSelectedLeadsData([]);
                                setCurrentPage(1);
                            }}
                            className={`flex-1 py-3 px-4 rounded-lg border-2 transition-colors ${sourceType === 'birthdays'
                                    ? 'border-green-500 bg-green-50 text-green-700'
                                    : 'border-gray-300 hover:border-gray-400'
                                }`}
                        >
                            <Gift className="h-5 w-5 mx-auto mb-1" />
                            Aniversariantes Hoje
                        </button>
                    </div>
                </div>

                {/* CSV Upload */}
                {sourceType === 'csv' && (
                    <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Arquivo CSV (máximo {MAX_SELECTIONS} destinatários)
                        </label>
                        <input
                            type="file"
                            accept=".csv"
                            onChange={handleFileUpload}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        />
                        <div className="flex items-center justify-between mt-2">
                            {csvData.length > 0 ? (
                                <p className="text-sm text-green-600">
                                    ✓ {csvData.length} destinatários carregados
                                </p>
                            ) : (
                                <span />
                            )}
                            <a
                                href="/exemplo-importacao.csv"
                                download="exemplo-importacao.csv"
                                className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-800 transition-colors"
                            >
                                <Download className="h-4 w-4" />
                                Baixar CSV de exemplo
                            </a>
                        </div>
                        <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                            <p className="text-xs font-semibold text-blue-800 mb-1.5">
                                📋 Colunas suportadas no CSV para variáveis do template:
                            </p>
                            <div className="flex flex-wrap gap-1.5">
                                {[
                                    { col: 'Nome', var: '{{nome}}' },
                                    { col: 'WhatsApp', var: '{{whatsapp}}' },
                                    { col: 'Email', var: '{{email}}' },
                                    { col: 'Aniversario', var: '{{aniversario}}' },
                                    { col: 'Premio', var: '{{premio}}' },
                                ].map(({ col, var: v }) => (
                                    <span key={col} className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-white border border-blue-200 text-xs text-blue-700">
                                        <span className="font-medium">{col}</span>
                                        <span className="text-blue-400">→</span>
                                        <span className="font-mono text-purple-600">{v}</span>
                                    </span>
                                ))}
                            </div>
                            <p className="text-xs text-blue-600 mt-1.5">
                                Colunas extras serão mapeadas como <code className="bg-white px-1 rounded text-purple-600">{'{{campo_NomeColuna}}'}</code>
                            </p>
                        </div>
                    </div>
                )}

                {/* Clientes Selection */}
                {(sourceType === 'leads' || sourceType === 'birthdays') && (
                    <div className="mb-6 space-y-4">
                        {/* Header Info */}
                        {sourceType === 'birthdays' && (
                            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                                <div className="flex items-center">
                                    <Gift className="h-5 w-5 text-green-600 mr-2" />
                                    <div>
                                        <h3 className="font-medium text-green-900">
                                            Aniversariantes de Hoje
                                        </h3>
                                        <p className="text-sm text-green-700 mt-1">
                                            Envie mensagens especiais para clientes que fazem aniversário hoje!
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Selected Leads Summary */}
                        {selectedLeadsData.length > 0 && (
                            <div className={`${sourceType === 'birthdays' ? 'bg-green-50 border-green-200' : 'bg-blue-50 border-blue-200'} border rounded-lg p-4`}>
                                <div className="flex items-center justify-between mb-2">
                                    <h3 className={`font-medium ${sourceType === 'birthdays' ? 'text-green-900' : 'text-blue-900'}`}>
                                        {sourceType === 'birthdays' ? 'Aniversariantes Selecionados' : 'Clientes Selecionados'} ({selectedLeadsData.length}/{MAX_SELECTIONS})
                                    </h3>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {selectedLeadsData.map((lead) => (
                                        <div
                                            key={lead.id}
                                            className={`inline-flex items-center bg-white ${sourceType === 'birthdays' ? 'border-green-300' : 'border-blue-300'} border rounded-full px-3 py-1 text-sm`}
                                        >
                                            <span className="mr-2">{lead.customer_name}</span>
                                            <button
                                                onClick={() => removeSelectedLead(lead.id)}
                                                className="text-red-500 hover:text-red-700"
                                            >
                                                <X className="h-4 w-4" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="flex items-center justify-between">
                            <label className="block text-sm font-medium text-gray-700">
                                Selecione os Clientes (máximo {MAX_SELECTIONS})
                            </label>
                            <div className="flex space-x-2">
                                <button
                                    onClick={handleSelectAllOnPage}
                                    disabled={selectedLeads.length >= MAX_SELECTIONS}
                                    className="text-sm text-blue-600 hover:text-blue-800 disabled:text-gray-400 disabled:cursor-not-allowed"
                                >
                                    Selecionar Todos (Página)
                                </button>
                                <span className="text-gray-300">|</span>
                                <button
                                    onClick={handleDeselectAllOnPage}
                                    className="text-sm text-red-600 hover:text-red-800"
                                >
                                    Desmarcar Todos (Página)
                                </button>
                            </div>
                        </div>

                        <div className="border border-gray-300 rounded-lg overflow-hidden">
                            {loadingLeads ? (
                                <div className="flex items-center justify-center py-12">
                                    <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                                </div>
                            ) : (
                                <>
                                    <div className="max-h-96 overflow-y-auto">
                                        {leads.map((lead) => (
                                            <div
                                                key={lead.id}
                                                className="flex items-center p-3 hover:bg-gray-50 border-b last:border-b-0"
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={selectedLeads.includes(lead.id)}
                                                    onChange={() => toggleLead(lead.id)}
                                                    disabled={!selectedLeads.includes(lead.id) && selectedLeads.length >= MAX_SELECTIONS}
                                                    className="mr-3"
                                                />
                                                <div className="flex-1">
                                                    <p className="font-medium">{lead.customer_name}</p>
                                                    <p className="text-sm text-gray-600">{lead.whatsapp}</p>
                                                    {lead.customer_email && (
                                                        <p className="text-xs text-gray-500">{lead.customer_email}</p>
                                                    )}
                                                </div>
                                            </div>
                                        ))}

                                        {
                                            !leads.length && (
                                                <div className="p-4 text-center text-gray-500">
                                                    Nenhum cliente encontrado.
                                                </div>
                                            )
                                        }
                                    </div>

                                    {/* Pagination */}
                                    {pagination.totalPages > 1 && (
                                        <div className="bg-gray-50 px-4 py-3 border-t border-gray-300 flex items-center justify-between">
                                            <div className="text-sm text-gray-700">
                                                Página {currentPage} de {pagination.totalPages} ({pagination.total} clientes)
                                            </div>
                                            <div className="flex space-x-2">
                                                <button
                                                    onClick={() => goToPage(currentPage - 1)}
                                                    disabled={currentPage === 1}
                                                    className="p-2 rounded-lg border border-gray-300 hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed"
                                                >
                                                    <ChevronLeft className="h-4 w-4" />
                                                </button>

                                                {/* Page Numbers */}
                                                <div className="flex space-x-1">
                                                    {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                                                        let pageNum: number;
                                                        if (pagination.totalPages <= 5) {
                                                            pageNum = i + 1;
                                                        } else if (currentPage <= 3) {
                                                            pageNum = i + 1;
                                                        } else if (currentPage >= pagination.totalPages - 2) {
                                                            pageNum = pagination.totalPages - 4 + i;
                                                        } else {
                                                            pageNum = currentPage - 2 + i;
                                                        }

                                                        return (
                                                            <button
                                                                key={pageNum}
                                                                onClick={() => goToPage(pageNum)}
                                                                className={`px-3 py-1 rounded-lg border ${currentPage === pageNum
                                                                        ? 'bg-blue-600 text-white border-blue-600'
                                                                        : 'border-gray-300 hover:bg-white'
                                                                    }`}
                                                            >
                                                                {pageNum}
                                                            </button>
                                                        );
                                                    })}
                                                </div>

                                                <button
                                                    onClick={() => goToPage(currentPage + 1)}
                                                    disabled={currentPage === pagination.totalPages}
                                                    className="p-2 rounded-lg border border-gray-300 hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed"
                                                >
                                                    <ChevronRight className="h-4 w-4" />
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                )}

                {/* Send Button */}
                <button
                    onClick={handleDispatch}
                    disabled={loading || !selectedTemplate}
                    className="w-full flex items-center justify-center px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {loading ? (
                        <>
                            <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                            Processando...
                        </>
                    ) : (
                        <>
                            <Send className="h-5 w-5 mr-2" />
                            Iniciar Disparo
                        </>
                    )}
                </button>
            </div>
        </div>
    );
}
